import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import CustomerPageHeader from '../../components/customer/CustomerPageHeader';
import CustomerPageLoading from '../../components/customer/CustomerPageLoading';
import CustomerPageSection from '../../components/customer/CustomerPageSection';
import CustomerPrimaryButton from '../../components/customer/CustomerPrimaryButton';
import { getConsentFormName } from '../../components/consent-forms/consentFormDisplay';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import { getLocalizedValue } from '../../constants/Languages';
import { useStore } from '../../context/StoreContext';
import useConsentGate from '../../hooks/useConsentGate';
import ConsentFormService, { ConsentContextType } from '../../services/consentFormService';
import { formatCalendarDate, formatClockTime } from '../../utils/dateFormatUtils';

const itemKey = (item) => item.appointmentId || item.customerServicePackageId || item.customerMembershipId;

/**
 * Where a customer accepts the consent forms still pending for bookings and purchases the store
 * made on their behalf. The link the store sends lands here.
 */
const CustomerConsentsPage = () => {
  const { t, i18n } = useTranslation();
  const { storeId } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { reviewForms, consentDialog } = useConsentGate(storeId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const autoOpenedRef = useRef(false);

  const loadItems = useCallback(async () => {
    if (!storeId) {
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const nextItems = await ConsentFormService.getMyPendingConsents(storeId);
      setItems(nextItems);
      return nextItems;
    } catch (loadError) {
      console.error('Failed to load pending consent forms:', loadError);
      setError(t('consentForms.customerPage.loadError'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [storeId, t]);

  const handleReview = useCallback(async (item) => {
    setFeedback(null);
    const result = await reviewForms(item.forms, {
      contextType: ConsentContextType.PendingRequest,
      appointmentId: item.appointmentId,
      contextItemId: item.customerServicePackageId || item.customerMembershipId,
    });

    if (result.accepted) {
      setFeedback(t('consentForms.customerPage.accepted'));
      await loadItems();
    }
  }, [loadItems, reviewForms, t]);

  useEffect(() => {
    loadItems().then((loadedItems) => {
      const appointmentId = searchParams.get('appointment');
      if (!appointmentId || autoOpenedRef.current) {
        return;
      }

      autoOpenedRef.current = true;
      searchParams.delete('appointment');
      setSearchParams(searchParams, { replace: true });
      const target = loadedItems.find((item) => item.appointmentId === appointmentId);
      if (target) {
        handleReview(target);
      }
    });
  }, [loadItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const describeItem = (item) => {
    if (item.contextType === ConsentContextType.Booking) {
      return t('consentForms.customerPage.appointment', {
        date: formatCalendarDate(item.appointmentDate),
        time: formatClockTime(item.appointmentStartTime),
      });
    }

    const name = getLocalizedValue(item.itemName, i18n.language, undefined, '');
    return item.contextType === ConsentContextType.ServicePackagePurchase
      ? t('consentForms.customerPage.package', { name })
      : t('consentForms.customerPage.membership', { name });
  };

  return (
    <Box id="customer-consents-page">
      <CustomerPageHeader
        icon={DescriptionIcon}
        title={t('consentForms.customerPage.title')}
        subtitle={t('consentForms.customerPage.subtitle')}
      />

      {feedback ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback}
        </Alert>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? (
        <CustomerPageLoading />
      ) : items.length === 0 ? (
        <CustomerPageSection variant="empty" sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: CUSTOMER_COLORS.text }}>
            {t('consentForms.customerPage.empty')}
          </Typography>
        </CustomerPageSection>
      ) : (
        <Stack spacing={2}>
          {items.map((item) => (
            <CustomerPageSection key={itemKey(item)} id={`customer-consent-item-${itemKey(item)}`}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text, mb: 1 }}>
                    {describeItem(item)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.forms.map((form) => (
                      <Chip
                        key={form.versionId}
                        size="small"
                        icon={<DescriptionIcon sx={{ color: `${BRAND.accentText} !important` }} />}
                        label={getConsentFormName(form.name, i18n.language, t)}
                        sx={{ backgroundColor: brandAlpha('accent', 0.12), color: BRAND.accentText, fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                </Box>
                <CustomerPrimaryButton id={`customer-consent-review-${itemKey(item)}`} onClick={() => handleReview(item)}>
                  {t('consentForms.customerPage.review')}
                </CustomerPrimaryButton>
              </Stack>
            </CustomerPageSection>
          ))}
        </Stack>
      )}

      {consentDialog}
    </Box>
  );
};

export default CustomerConsentsPage;
